import { isObject } from 'util';

import { Inject } from '@odinjs/odin';

import { catchRenderException, Endpoint, ReportService, ToastServiceStipulator } from '@philips-emr/hpms-stipulator-commons';
import StipulatorContext from '@philips-emr/hpms-stipulator-commons/lib/contexts/stipulator-context';
import { Report } from '@philips-emr/hpms-stipulator-components';
import HpmsCustomDialogAPI from '@philips-emr/hpms-stipulator-components/lib/api/hpms-custom-dialog-api';
import { ActionAPI, FetchAPI, FieldAPI, FieldsetAPI, FileUploaderAPI, FormAPI, PanelAPI, TextAPI, ToastNotificationAPI } from '@philips-emr/tws-components';
import Controller, { OnAfterFetch, OnBeforeFetch, OnBeforeSubmit, OnBlur, OnChange, OnClick, OnClose, OnEdit, OnMount } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, EditEvent, FetchEvent, MountEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { RequestResponse } from '@philips-emr/tws-core/lib/services/http-service';
import { isContentfulString, isEmptyObject } from '@philips-emr/tws-utils';

import { FragmentNames } from '../../codegen';
import { PersonDataDetailsFormEditableAliases as Aliases } from '../../codegen/constants/aliases';
import { Anexo } from '../../interfaces/anexo';
import { EnderecoData } from '../../interfaces/logradouro';
import { Mensagem } from '../../interfaces/mensagem';
import BaseController from '../BaseController';

import './personal-data.css';

interface Target {
  state: State;
}

interface State {
  record: Record<string, Value>;
  oldValues: Record<string, Value>;
}

interface Value {
  dsMunicipio: string;
  vlDominio: string;
  nrSequencia: string;
  ieSituacao: string;
}

const ieFuncionalidade = '11';

@Controller({ fragment: FragmentNames.PERSON_DATA_DETAILS_FORM_EDITABLE, component: Aliases.PERSON_DATA_DETAILS_LAYOUT_EDITABLE })
class PersonalDataController extends BaseController {

  state: State = { oldValues: {}, record: {} };

  private regraAnexos: Anexo[];

  @Inject(Aliases.FIELD_NR_TERMO_ALT)
  private fieldNrTermoAlt: FieldAPI;
  @Inject(Aliases.FIELDSET_ANEXOS)
  private fieldsetAnexos: FieldsetAPI;
  @Inject(Aliases.PERSON_DATA_FORM_EDITABLE)
  private personDataFormEditable: FormAPI;
  @Inject(Aliases.MUNICIPIO_FETCH)
  private municipioFetch: FetchAPI;
  @Inject(Aliases.PANEL_LOADER)
  private panelLoader: PanelAPI;
  @Inject(Aliases.FILE_UPLOADER)
  private fileUploaderAPI: FileUploaderAPI;
  @Inject(Aliases.HPMS_CUSTOM_DIALOG)
  private customDialog: HpmsCustomDialogAPI;
  @Inject(Aliases.TEXT_PROTOCOLO)
  private textProtocolo: TextAPI;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toastForm: ToastNotificationAPI;
  @Inject(Aliases.FIELD_FILE_UPLOADER)
  private fieldUpload: FieldAPI;
  @Inject(Aliases.ACTION_PRINT)
  private buttonPrint: ActionAPI;
  @Inject(Aliases.PERSON_DATA_FORM_EDITABLE)
  private formEditable: FormAPI;

  private reportService: ReportService;

  @OnMount(Aliases.PERSON_DATA_FETCH_EDITABLE)
  onMount(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { cdPessoaFisica, nrSeqBeneficiario } = this.historyService.getCurrentRouteParams();

    api.setParameters({ cdPessoaFisica, nrSeqBeneficiario });
    api.forceFetch();
  }

  @OnBeforeFetch(Aliases.PERSON_DATA_FETCH_EDITABLE)
  onBeforeFetchBeneficiarios(event: MountEvent): void {
    const fetch = event.target as unknown as FetchAPI;
    const { nrSeqSubestipulante } = StipulatorContext.getCurrentContract();
    nrSeqSubestipulante && fetch.setParameters({ ...fetch.getParameters(), nrSeqSubestipulante });
  }

  @OnAfterFetch(Aliases.PERSON_DATA_FETCH_EDITABLE)
  onAfterFetchBeneficiarios(event: FetchEvent): void {
    const { ieSituacao } = event.data as Value;

    this.formEditable.setLocked(ieSituacao === 'I');
  }

  @OnMount(Aliases.ACTION_PRINT)
  onMountAction(): void {

    this.reportService = new ReportService('22', Endpoint.GET.RELATORIOS_POR_GRUPO);
    void this.reportService.getRelatoriosByCdGrupo()
      .then((response: Report[]) => {
        if (response.length === 0) {
          this.buttonPrint.setEnabled(false);
        }
      });
  }

  @OnClick(Aliases.ACTION_PRINT)
  onClickPrint(): void {
    const { nrSeqBeneficiario } = this.historyService.getCurrentRouteParams();
    this.reportService = new ReportService('22', Endpoint.GET.RELATORIOS_POR_GRUPO);
    this.reportService.abreListaRelatorios({ NR_SEQ_SEGURADO: nrSeqBeneficiario });
  }

  @OnEdit(Aliases.PERSON_DATA_FORM_EDITABLE)
  onEdit(event: EditEvent): void {
    const target = event.target as unknown as Target;
    this.state.oldValues = target.state.record;

    this.checkTipoAnexo();
  }

  @OnBeforeSubmit(Aliases.PERSON_DATA_FORM_EDITABLE)
  onBeforeSubmitEvent(event: SubmitEvent): void {
    this.panelLoader.setVisible(true);

    const { cdPessoaFisica, nrSeqBeneficiario, nrSeqContrato } = this.historyService.getCurrentRouteParams();

    const newValues = event.record as Record<string, Value>;
    const oldValues = this.state.oldValues;

    const campos: object[] = [];
    const nrSeqTermo = this.fieldNrTermoAlt.getValue();
    const body = { nrSeqBeneficiario, cdPessoaFisica, nrSeqTermo, campos, dadosBeneficiario: event.record };

    if (!body.nrSeqTermo) {
      delete body.nrSeqTermo;
    }

    Object.keys(newValues).forEach(key => {
      if (oldValues[key] !== newValues[key]) {
        const oldV = oldValues[key];
        const newV = newValues[key];
        if (key !== 'termo' && key !== 'anexos') {
          body.campos.push({
            nmAtributo: key,
            dsValorOld: oldV ? oldV.vlDominio || oldV.nrSequencia || oldV.dsMunicipio || oldV : '',
            dsValorNew: newV ? newV.vlDominio || newV.nrSequencia || newV.dsMunicipio || newV : '',
          });
        }
      }
    });

    const anexos = body.dadosBeneficiario.anexos as Anexo[];
    body.dadosBeneficiario.anexos = this.handleAnexos(anexos);

    HttpService.post(Endpoint.POST.SOLIC_ALT_CADASTRO, { nrSeqContrato, nrSeqBeneficiario, cdPessoaFisica }, { body })
      .then((response) => {
        const data = response.body as Mensagem;

        ToastServiceStipulator.information(data[Object.keys(data)[0]]);
        this.panelLoader.setVisible(false);
      }).catch(response => {
        const openToast = (msg: string): void => {
          this.customDialog.setVisible(true);
          this.textProtocolo.setValue(msg);
          this.panelLoader.setVisible(false);
          this.personDataFormEditable.setDisabled(false);
          this.personDataFormEditable.setEditing(true);
        };

        const { body } = response;

        if (Array.isArray(body)) {
          const data = body as Anexo[];
          const message = `${MessageService.translate('stipulator.obrigatorio-anexar-arquivo')} ${data.map(anexo => anexo.dsLabelCampo).join(', ')}.`;
          this.personDataFormEditable.setEditing(true);
          this.toastForm.setVisible(true);
          this.toastForm.setMessage(message);
          this.panelLoader.setVisible(false);
          this.checkTipoAnexo();
          return;
        }

        if (isObject(body) && !isEmptyObject(body)) {
          return openToast(body[Object.keys(body)[0]]);
        }

        if (isContentfulString(body)) {
          return openToast(body);
        }
      });
  }

  @OnChange(Aliases.FIELD_FETCH_ESTADOS)
  onChangeEstado(e: ChangeEvent<string>): void {
    this.municipioFetch.setParameters({ dsUnidadeFederacao: e.value });
    this.municipioFetch.forceFetch();
  }

  @OnClose(Aliases.TOAST_NOTIFICATION)
  onCloseToast(): void {
    this.toastForm.setVisible(false);
  }

  @OnBlur(Aliases.FIELD_CEP)
  onBlurCep(e: FocusEvent): void {
    const elem = e.target as unknown as FieldAPI;
    const cdCep = (elem.getValue && elem.getValue()) || elem.state.value as number;

    if (cdCep) {
      void HttpService.get<EnderecoData>(Endpoint.GET.ENDERECO_CEP, { cdCep })
        .then((response) => this.setEndereco(response));
    }
  }

  private setEndereco(response: RequestResponse<EnderecoData>): void {
    if (response.body) {
      const { dsUf, dsEndereco, dsCidade, cdMunicipioIbge } = response.body;

      this.personDataFormEditable.setData({
        ...this.personDataFormEditable.getRecord(),
        sgEstado: dsUf,
        dsEndereco,
        dsMunicipio: dsCidade,
        cdMunicipioIbge,
      });
    }
  }

  private checkTipoAnexo(): void {
    void HttpService.get(Endpoint.GET.CHECK_TIPO_ANEXOS, { ieFuncionalidade })
      .then(response => {

        this.fieldsetAnexos.setVisible(true);
        const anexos = response.body as Anexo[];

        const predefinedFiles = anexos.map(regras => {
          const { dsLabelCampo: value, nrSeqRegraAnexo: key } = regras;

          return { key: String(key), value };
        });
        this.regraAnexos = anexos;
        catchRenderException(() => this.fileUploaderAPI.setPredefinedFiles(predefinedFiles));

        if (predefinedFiles.length == 0) {
          this.fieldUpload.setVisible(false);
        }
      });
  }

  private handleAnexos(anexos: Anexo[]): Anexo[] {
    const handledAnexos = anexos?.map(anexo => {
      const handledAnexo = { ...anexo };
      const predefinedFileKey = anexo.predefinedFileKey;
      const nrSeqTipoDocumento = this.regraAnexos.find(regraAnexo => +regraAnexo.nrSeqRegraAnexo === +predefinedFileKey)?.nrSeqTipoDocumento ?? '0';
      handledAnexo.nrSeqTipoDocumento = String(nrSeqTipoDocumento);
      return handledAnexo;
    });

    return handledAnexos;
  }

}

export default PersonalDataController;
